import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { 
  createAdminNotification, 
  sendAdminNotification, 
  getAdminNotifications, 
  deleteAdminNotification,
  NOTIFICATION_TYPES,
  TARGET_AUDIENCES 
} from '@/lib/adminNotifications';
import { 
  Send, 
  Trash2, 
  Eye, 
  Users, 
  Calendar, 
  MessageSquare,
  AlertCircle,
  Megaphone,
  Info,
  Gift,
  Filter,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  RefreshCw
} from 'lucide-react';
import { formatUkDateTime } from '@/lib/adminFormat';

const AdminNotificationManager = () => {
  const { isAdminAuthenticated } = useAdminAuth();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // State for creating new notifications
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: NOTIFICATION_TYPES.ANNOUNCEMENT,
    targetAudience: TARGET_AUDIENCES.ALL,
    targetUserIds: []
  });
  
  // State for managing notifications
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, draft, sent, failed
  const [filterType, setFilterType] = useState('all'); // all, or specific type
  const [searchTerm, setSearchTerm] = useState('');

  // Load notifications on component mount
  useEffect(() => {
    console.log('🔍 AdminNotificationManager: Loading notifications', { 
      isAdminAuthenticated, 
      user: !!user,
      userUid: user?.uid,
      userEmail: user?.email 
    });
    if (isAdminAuthenticated) {
      loadNotifications();
    }
  }, [isAdminAuthenticated, user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const notificationsData = await getAdminNotifications();
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load notifications",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    
    if (!newNotification.title.trim() || !newNotification.message.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in both title and message",
      });
      return;
    }

    try {
      setLoading(true);
      
      const notification = await createAdminNotification({
        ...newNotification,
        createdBy: user?.uid || 'admin'
      });
      
      toast({
        title: "Success",
        description: "Notification created successfully",
      });
      
      // Reset form
      setNewNotification({
        title: '',
        message: '',
        type: NOTIFICATION_TYPES.ANNOUNCEMENT,
        targetAudience: TARGET_AUDIENCES.ALL,
        targetUserIds: []
      });
      
      setShowCreateForm(false);
      loadNotifications();
      
    } catch (error) {
      console.error('Error creating notification:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create notification",
      });
    } finally {
      setLoading(false);
    }
  };

  const audienceToastLabel = (audience) => {
    switch (audience) {
      case TARGET_AUDIENCES.ARTISTS:
        return 'artists';
      case TARGET_AUDIENCES.KARAOKE:
        return 'karaoke hosts';
      case TARGET_AUDIENCES.SPECIFIC:
        return 'recipients';
      case TARGET_AUDIENCES.ALL:
        return 'users';
      default:
        return 'users';
    }
  };

  const handleSendNotification = async (notificationId) => {
    try {
      setSending(true);
      
      const result = await sendAdminNotification(notificationId);
      const label = audienceToastLabel(result.targetAudience);
      let description;
      if (result.totalTargets === 0) {
        description = `No profiles matched this audience. Nothing was published.`;
      } else {
        const published = `Published to ${result.totalTargets} ${label}.`;
        if (result.sentCount > 0) {
          const pushNote =
            result.failedCount > 0
              ? ` Web push reached ${result.sentCount}; ${result.failedCount} without push or failed.`
              : ` Web push reached ${result.sentCount}.`;
          description = published + pushNote;
        } else {
          description =
            published +
            ` Web push: 0 (no active push subscriptions). The in-app notification still appears for signed-in ${label}.`;
        }
      }

      toast({
        title: "Notification Sent",
        description,
      });
      
      loadNotifications();
      
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send notification",
      });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await deleteAdminNotification(notificationId);
      
      toast({
        title: "Success",
        description: "Notification deleted successfully",
      });
      
      loadNotifications();
      
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete notification",
      });
    }
  };

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return <Megaphone className="h-4 w-4" />;
      case NOTIFICATION_TYPES.UPDATE:
        return <Info className="h-4 w-4" />;
      case NOTIFICATION_TYPES.WARNING:
        return <AlertCircle className="h-4 w-4" />;
      case NOTIFICATION_TYPES.PROMOTION:
        return <Gift className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return 'bg-blue-100 text-blue-800';
      case NOTIFICATION_TYPES.UPDATE:
        return 'bg-green-100 text-green-800';
      case NOTIFICATION_TYPES.WARNING:
        return 'bg-yellow-100 text-yellow-800';
      case NOTIFICATION_TYPES.PROMOTION:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTargetAudienceLabel = (audience) => {
    switch (audience) {
      case TARGET_AUDIENCES.ALL:
        return 'All Users';
      case TARGET_AUDIENCES.ARTISTS:
        return 'Artists Only';
      case TARGET_AUDIENCES.KARAOKE:
        return 'Karaoke Hosts Only';
      case TARGET_AUDIENCES.SPECIFIC:
        return 'Specific Users';
      default:
        return audience;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not sent';
    return formatUkDateTime(date);
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    // Status filter
    if (filterStatus !== 'all' && notification.status !== filterStatus) {
      return false;
    }
    
    // Type filter
    if (filterType !== 'all' && notification.type !== filterType) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        notification.title?.toLowerCase().includes(searchLower) ||
        notification.message?.toLowerCase().includes(searchLower) ||
        getTargetAudienceLabel(notification.targetAudience).toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'draft':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  // Don't render if not authenticated
  if (!isAdminAuthenticated) {
    console.log('🔍 AdminNotificationManager: Not authenticated, showing loading...', { isAdminAuthenticated });
    return (
      <div className="text-center py-8 text-gray-500">
        Loading admin notification system...
      </div>
    );
  }

  console.log('🔍 AdminNotificationManager: Authenticated, rendering component...', { isAdminAuthenticated, user: !!user });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Admin Notifications</h2>
          <p className="text-gray-400 text-sm mt-1">Create and send notifications to artists and karaoke hosts</p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-4 w-4" />
          {showCreateForm ? 'Cancel' : 'Create Notification'}
        </Button>
      </div>

      {/* Create Notification Form */}
      {showCreateForm && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Create New Notification</CardTitle>
            <CardDescription className="text-gray-400">
              Create a notification to send to your users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateNotification} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Title</label>
                  <Input
                    value={newNotification.title}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title"
                    required
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Type</label>
                  <Select
                    value={newNotification.type}
                    onValueChange={(value) => setNewNotification(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NOTIFICATION_TYPES.ANNOUNCEMENT}>
                        <div className="flex items-center gap-2">
                          <Megaphone className="h-4 w-4" />
                          Announcement
                        </div>
                      </SelectItem>
                      <SelectItem value={NOTIFICATION_TYPES.UPDATE}>
                        <div className="flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Update
                        </div>
                      </SelectItem>
                      <SelectItem value={NOTIFICATION_TYPES.WARNING}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Warning
                        </div>
                      </SelectItem>
                      <SelectItem value={NOTIFICATION_TYPES.PROMOTION}>
                        <div className="flex items-center gap-2">
                          <Gift className="h-4 w-4" />
                          Promotion
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Message</label>
                <Textarea
                  value={newNotification.message}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Notification message"
                  rows={4}
                  required
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Target Audience</label>
                <Select
                  value={newNotification.targetAudience}
                  onValueChange={(value) => setNewNotification(prev => ({ ...prev, targetAudience: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TARGET_AUDIENCES.ALL}>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        All Users
                      </div>
                    </SelectItem>
                    <SelectItem value={TARGET_AUDIENCES.ARTISTS}>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Artists Only
                      </div>
                    </SelectItem>
                    <SelectItem value={TARGET_AUDIENCES.KARAOKE}>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Karaoke Hosts Only
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? 'Creating...' : 'Create Notification'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      {!showCreateForm && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search notifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40 bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-48 bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.ANNOUNCEMENT}>Announcement</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.UPDATE}>Update</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.WARNING}>Warning</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.PROMOTION}>Promotion</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.SYSTEM}>System</SelectItem>
                    <SelectItem value={NOTIFICATION_TYPES.SUPPORT_RESPONSE}>Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {filteredNotifications.length === notifications.length 
              ? `All Notifications (${notifications.length})`
              : `Filtered Notifications (${filteredNotifications.length} of ${notifications.length})`
            }
          </h3>
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={loadNotifications}
              disabled={loading}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading notifications...</div>
        ) : filteredNotifications.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-8 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">
                {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                  ? 'No notifications match your filters'
                  : 'No notifications created yet'
                }
              </p>
              {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setFilterType('all');
                  }}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <Card key={notification.id} className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">
                        {getNotificationTypeIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white text-lg mb-1">{notification.title}</h4>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="border-purple-600 text-purple-400 text-xs">
                            {getTargetAudienceLabel(notification.targetAudience)}
                          </Badge>
                          <Badge className={`${getNotificationTypeColor(notification.type)} text-xs`}>
                            {notification.type}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            {getStatusIcon(notification.status)}
                            <span className="capitalize">{notification.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-4 whitespace-pre-wrap">{notification.message}</p>
                  
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-gray-700">
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>Created: {formatDate(notification.createdAt)}</span>
                      </div>
                      {notification.sentAt && (
                        <div className="flex items-center gap-1">
                          <Send className="h-4 w-4" />
                          <span>Sent: {formatDate(notification.sentAt)}</span>
                        </div>
                      )}
                      {(() => {
                        const denom =
                          typeof notification.recipientCount === 'number' && notification.recipientCount > 0
                            ? notification.recipientCount
                            : notification.totalSent || 0;
                        const reads = notification.readCount ?? 0;
                        if (notification.status !== 'sent' || denom <= 0) return null;
                        return (
                          <div className="flex items-center gap-1" title="Acknowledged or dismissed in dashboard vs users targeted">
                            <Eye className="h-4 w-4" />
                            <span className="font-semibold text-green-400">
                              {reads}/{denom} read
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="flex gap-2">
                      {notification.status === 'draft' && (
                        <Button
                          size="sm"
                          onClick={() => handleSendNotification(notification.id)}
                          disabled={sending}
                          className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
                        >
                          <Send className="h-4 w-4" />
                          {sending ? 'Sending...' : 'Send Now'}
                        </Button>
                      )}
                      {notification.status === 'sent' &&
                        ((notification.recipientCount != null && notification.recipientCount > 0) ||
                          (notification.totalSent ?? 0) > 0) && (
                          <div className="text-xs text-gray-400 flex items-center gap-1 px-3 py-1.5 rounded bg-gray-700">
                            <CheckCircle className="h-3 w-3 text-green-400" />
                            {notification.recipientCount != null && notification.recipientCount > 0 ? (
                              <span>
                                Targeted {notification.recipientCount} user
                                {notification.recipientCount !== 1 ? 's' : ''}
                                {notification.totalSent > 0 &&
                                notification.recipientCount !== notification.totalSent ? (
                                  <span className="text-gray-500">
                                    {' '}
                                    · push {notification.totalSent}
                                  </span>
                                ) : null}
                              </span>
                            ) : (
                              <span>
                                Push delivered to {notification.totalSent} user
                                {notification.totalSent !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="flex items-center gap-1 border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationManager;
